import type { LenderConfigData, LenderConfigMap } from '../../../types/lenderTypes'
import type { BalanceData, AprData } from '../../user-data/fetch-balances/types'

export type { BalanceData, AprData, LenderConfigData, LenderConfigMap }

export interface PostTradeMetrics {
  pre: {
    healthFactor: number
    borrowCapacity: number
  }
  post: {
    healthFactor: number
    borrowCapacity: number
    balanceData: BalanceData
    aprData?: AprData
  }
}

export const EMPTY_BALANCE: BalanceData = {
  rewards: undefined,
  borrowDiscountedCollateral: 0,
  borrowDiscountedCollateralAllActive: 0,
  collateral: 0,
  collateralAllActive: 0,
  deposits: 0,
  debt: 0,
  adjustedDebt: 0,
  nav: 0,
  deposits24h: 0,
  debt24h: 0,
  nav24h: 0,
}

const positivePart = (x: number) => (x > 0 ? x : 0)

export function getHealthFactor(collateral: number, adjustedDebt: number): number {
  if (adjustedDebt === 0) return Infinity
  const raw = collateral / adjustedDebt
  return Number.isNaN(raw) ? 0 : Math.round(raw * 1e4) / 1e4
}

export function getBorrowCapacity(bd: BalanceData): number {
  return bd.borrowDiscountedCollateral - bd.adjustedDebt
}

export function getAssetConfig(config: LenderConfigMap, modeId: string): LenderConfigData {
  const entry = config[modeId]
  if (!entry) {
    throw new Error(`No config found for modeId ${modeId}`)
  }
  return entry
}

export function computePostTradeMetrics(
  balanceData: BalanceData,
  postTrade: BalanceData
): PostTradeMetrics {
  const healthFactor = getHealthFactor(balanceData.collateral, balanceData.adjustedDebt)
  const healthFactorPost =
    postTrade.adjustedDebt === 0
      ? Infinity
      : positivePart(getHealthFactor(postTrade.collateral, postTrade.adjustedDebt))

  return {
    pre: {
      healthFactor,
      borrowCapacity: getBorrowCapacity(balanceData),
    },
    post: {
      healthFactor: healthFactorPost,
      borrowCapacity: getBorrowCapacity(postTrade),
      balanceData: postTrade,
    },
  }
}
